import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Message, SettingsCard } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { ProjectContext } from "../../providers/ProjectProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./LicensingSettings.prefix.css";

/**
 * Licensing decision-tree wizard — Project Settings → Licensing.
 *
 * Walks the project owner through 1–5 questions and recommends one
 * of nine license outcomes (the seven Creative Commons licenses plus
 * proprietary / restricted). The chosen license, the reasoning trail
 * (which branches the owner took), and the chooser's user id all
 * persist as a small JSON blob on ``Project.license`` so the dataset
 * provenance is auditable.
 *
 * Why a tree at all: most dataset owners haven't read the CC license
 * matrix, and the upstream wording (BY / NC / SA / ND combos) is not
 * obvious from the names. A handful of plain-English questions
 * ("public?", "commercial use?", "derivatives?", "share-alike?")
 * narrows the options without asking the owner to compare the seven
 * by hand.
 *
 * The decision tree (8 questions, 9 outcomes) — also documented in
 * the .claude/memory grant-alignment doc:
 *
 *     start ──► public?
 *               yes ──► attribution? ──► no  ──► CC0
 *                                       yes ──► commercial?
 *                                                 yes ──► derivatives?
 *                                                           yes ──► share-alike? ── yes ──► CC-BY-SA
 *                                                                                  ── no  ──► CC-BY
 *                                                           no ──► CC-BY-ND
 *                                                 no ──► derivatives-nc?
 *                                                           yes ──► share-alike-nc? ── yes ──► CC-BY-NC-SA
 *                                                                                       ── no  ──► CC-BY-NC
 *                                                           no ──► CC-BY-NC-ND
 *               no ──► private-kind?
 *                       internal team ──► proprietary
 *                       gated access  ──► restricted-use
 */

export const LICENSES = {
  "CC0-1.0": {
    identifier: "CC0-1.0",
    name: "CC0 1.0. Public Domain Dedication",
    url: "https://creativecommons.org/publicdomain/zero/1.0/",
    summary: "Use freely, including commercially, with no restrictions and no required credit.",
    requires_attribution: false,
    restricts_commercial: false,
    restricts_derivatives: false,
    requires_share_alike: false,
  },
  "CC-BY-4.0": {
    identifier: "CC-BY-4.0",
    name: "Creative Commons Attribution 4.0 (CC BY)",
    url: "https://creativecommons.org/licenses/by/4.0/",
    summary: "Use freely, including commercially, as long as you credit the original dataset.",
    requires_attribution: true,
    restricts_commercial: false,
    restricts_derivatives: false,
    requires_share_alike: false,
  },
  "CC-BY-SA-4.0": {
    identifier: "CC-BY-SA-4.0",
    name: "Creative Commons Attribution-ShareAlike 4.0 (CC BY-SA)",
    url: "https://creativecommons.org/licenses/by-sa/4.0/",
    summary: "Use freely, including commercially, with credit. Derivative works must use the same license.",
    requires_attribution: true,
    restricts_commercial: false,
    restricts_derivatives: false,
    requires_share_alike: true,
  },
  "CC-BY-ND-4.0": {
    identifier: "CC-BY-ND-4.0",
    name: "Creative Commons Attribution-NoDerivatives 4.0 (CC BY-ND)",
    url: "https://creativecommons.org/licenses/by-nd/4.0/",
    summary:
      "Use and redistribute freely, including commercially, as long as you credit the original AND don't modify it.",
    requires_attribution: true,
    restricts_commercial: false,
    restricts_derivatives: true,
    requires_share_alike: false,
  },
  "CC-BY-NC-4.0": {
    identifier: "CC-BY-NC-4.0",
    name: "Creative Commons Attribution-NonCommercial 4.0 (CC BY-NC)",
    url: "https://creativecommons.org/licenses/by-nc/4.0/",
    summary: "Non-commercial use only, with credit. Modifications allowed.",
    requires_attribution: true,
    restricts_commercial: true,
    restricts_derivatives: false,
    requires_share_alike: false,
  },
  "CC-BY-NC-SA-4.0": {
    identifier: "CC-BY-NC-SA-4.0",
    name: "Creative Commons Attribution-NonCommercial-ShareAlike 4.0 (CC BY-NC-SA)",
    url: "https://creativecommons.org/licenses/by-nc-sa/4.0/",
    summary: "Non-commercial use only, with credit. Derivative works must use the same license.",
    requires_attribution: true,
    restricts_commercial: true,
    restricts_derivatives: false,
    requires_share_alike: true,
  },
  "CC-BY-NC-ND-4.0": {
    identifier: "CC-BY-NC-ND-4.0",
    name: "Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 (CC BY-NC-ND)",
    url: "https://creativecommons.org/licenses/by-nc-nd/4.0/",
    summary: "Non-commercial use only. Redistribute as-is, with credit. No modifications.",
    requires_attribution: true,
    restricts_commercial: true,
    restricts_derivatives: true,
    requires_share_alike: false,
  },
  proprietary: {
    identifier: "proprietary",
    name: "Proprietary (no public release)",
    url: "",
    summary: "Internal-team use only. Not redistributed; not shared outside the project's organisation.",
    requires_attribution: false,
    restricts_commercial: true,
    restricts_derivatives: true,
    requires_share_alike: false,
  },
  "restricted-use": {
    identifier: "restricted-use",
    name: "Restricted-use (gated access)",
    url: "",
    summary: "Released only to specific approved parties under a custom data-use agreement.",
    requires_attribution: true,
    restricts_commercial: true,
    restricts_derivatives: false,
    requires_share_alike: false,
  },
};

// Decision tree. Each node is either a question (with options that
// either point at another node or at ``result:<license-key>``) or
// implicitly a result (whenever ``next`` starts with ``result:``).
// Adding a new branch = drop in a new node and a new option pointing
// at it; no other code changes needed.
const TREE = {
  start: {
    text: "Will this dataset be made available outside your team?",
    hint: 'Pick "Yes" if any external party, researchers, the public, partner orgs, will receive a copy. "No" means internal team only.',
    options: [
      { label: "Yes, share publicly or with external parties", next: "attribution", reasoning: "Public release" },
      { label: "No, internal use only", next: "private_kind", reasoning: "Private dataset" },
    ],
  },
  attribution: {
    text: "Should reusers credit you when they use this dataset?",
    hint: 'Most academic / shared datasets require attribution. "No" releases the data into the public domain, anyone can use it without naming you.',
    options: [
      { label: "Yes, require credit", next: "commercial", reasoning: "Attribution required" },
      { label: "No, dedicate to the public domain", next: "result:CC0-1.0", reasoning: "Public domain dedication" },
    ],
  },
  commercial: {
    text: "Allow commercial use?",
    hint: "Commercial includes for-profit companies, paid research, and products built on top of the dataset.",
    options: [
      { label: "Yes, including commercial use", next: "derivatives", reasoning: "Commercial use allowed" },
      { label: "No, non-commercial only", next: "derivatives_nc", reasoning: "Non-commercial only" },
    ],
  },
  derivatives: {
    text: "Allow modifications and derivative works?",
    hint: "Derivatives = translations, augmentations, fine-tuned models, mash-ups with other data.",
    options: [
      { label: "Yes, allow modifications", next: "share_alike", reasoning: "Derivatives allowed" },
      { label: "No, redistribute the dataset as-is", next: "result:CC-BY-ND-4.0", reasoning: "No derivatives" },
    ],
  },
  derivatives_nc: {
    text: "Allow modifications and derivative works?",
    hint: "Same question as before, applies regardless of commercial / non-commercial.",
    options: [
      { label: "Yes, allow modifications", next: "share_alike_nc", reasoning: "Derivatives allowed" },
      { label: "No, redistribute the dataset as-is", next: "result:CC-BY-NC-ND-4.0", reasoning: "No derivatives" },
    ],
  },
  share_alike: {
    text: 'Require derivatives to use the same license? ("copyleft")',
    hint: "Share-alike means anyone who modifies your dataset must release THEIR version under the same license. Encourages a shared open ecosystem.",
    options: [
      {
        label: "Yes, derivatives must use this same license",
        next: "result:CC-BY-SA-4.0",
        reasoning: "Share-alike (copyleft)",
      },
      { label: "No, derivatives can choose any license", next: "result:CC-BY-4.0", reasoning: "No share-alike" },
    ],
  },
  share_alike_nc: {
    text: 'Require derivatives to use the same license? ("copyleft")',
    hint: "Share-alike on a non-commercial license keeps everything in the non-commercial ecosystem.",
    options: [
      {
        label: "Yes, derivatives must use this same license",
        next: "result:CC-BY-NC-SA-4.0",
        reasoning: "Share-alike (copyleft)",
      },
      { label: "No, derivatives can choose any license", next: "result:CC-BY-NC-4.0", reasoning: "No share-alike" },
    ],
  },
  private_kind: {
    text: "How will access be controlled?",
    hint: '"Internal" means the dataset stays inside your organisation. "Restricted-use" releases it to specific approved external parties under a custom agreement.',
    options: [
      { label: "Internal team only", next: "result:proprietary", reasoning: "Internal-only access" },
      {
        label: "Specific external parties, gated access",
        next: "result:restricted-use",
        reasoning: "Gated external release",
      },
    ],
  },
};

const css = cn("licensing");

/** A single chip rendered for the current question's options. The
 *  caller maps the option's ``next`` to either advance the cursor or
 *  finalise the result. */
const OptionButton = ({ children, onClick }) => (
  <Button
    type="button"
    look="outlined"
    size="small"
    onClick={onClick}
    aria-label={typeof children === "string" ? children : undefined}
  >
    {children}
  </Button>
);

/** Renders the wizard's progression: a stack of breadcrumbs showing
 *  every (question, answer) pair the user has confirmed so far,
 *  with an "edit" affordance on each so they can step back to any
 *  earlier branch without restarting. */
const ReasoningTrail = ({ trail, onJumpTo }) => {
  if (!trail.length) return null;
  return (
    <ol className={css.elem("trail").toClassName()}>
      {trail.map((step, idx) => (
        <li key={idx} className={css.elem("trail-step").toClassName()}>
          <span className={css.elem("trail-q").toClassName()}>{step.q}</span>
          <span className={css.elem("trail-a").toClassName()}>{step.a}</span>
          <Button
            type="button"
            look="string"
            variant="neutral"
            size="smaller"
            className={css.elem("trail-edit").toClassName()}
            onClick={() => onJumpTo(idx)}
            aria-label={`Edit answer to "${step.q}"`}
          >
            Edit
          </Button>
        </li>
      ))}
    </ol>
  );
};

/** Big card surfacing the recommended license + the "save it" CTA.
 *  Until the owner clicks Save, the wizard's recommendation is just
 *  a suggestion — Project.license stays empty. */
const RecommendationCard = ({ license, onSave, onRestart, saving }) => (
  <div className={css.elem("recommend").toClassName()}>
    <h3 className={css.elem("recommend-name").toClassName()}>{license.name}</h3>
    <p className={css.elem("recommend-summary").toClassName()}>{license.summary}</p>
    <ul className={css.elem("recommend-attrs").toClassName()}>
      <li>{license.requires_attribution ? "Requires" : "Does not require"} attribution</li>
      <li>{license.restricts_commercial ? "Disallows" : "Allows"} commercial use</li>
      <li>{license.restricts_derivatives ? "Disallows" : "Allows"} derivatives</li>
      <li>{license.requires_share_alike ? "Requires" : "Does not require"} share-alike (copyleft)</li>
    </ul>
    {license.url ? (
      <a href={license.url} target="_blank" rel="noreferrer" className={css.elem("recommend-link").toClassName()}>
        Read the full license text →
      </a>
    ) : null}
    <div className={css.elem("recommend-actions").toClassName()}>
      <Button type="button" variant="primary" onClick={onSave} disabled={saving}>
        {saving ? "Saving…" : "Set as project license"}
      </Button>
      <Button type="button" look="outlined" onClick={onRestart} disabled={saving}>
        Restart wizard
      </Button>
    </div>
  </div>
);

/** The "current license" surface shown at the top of the page when
 *  Project.license is non-empty. Lists the chosen license + the
 *  reasoning trail snapshot so any team member opening the page can
 *  understand WHY that license was picked. */
const CurrentLicenseCard = ({ saved, onClear, busy }) => {
  const { t } = useTranslation();
  if (!saved || !saved.identifier) return null;
  return (
    <SettingsCard
      title={t("pages.settings.licensing_settings.current_license")}
      description={saved.name || saved.identifier}
    >
      <p className={css.elem("current-summary").toClassName()}>{saved.summary || "(no summary recorded)"}</p>
      {saved.url ? (
        <a href={saved.url} target="_blank" rel="noreferrer" className={css.elem("recommend-link").toClassName()}>
          {saved.url}
        </a>
      ) : null}
      {Array.isArray(saved.reasoning) && saved.reasoning.length ? (
        <details className={css.elem("current-trail").toClassName()}>
          <summary>{t("pages.settings.licensing_settings.reasoning_trail_why_this_license_was_cho")}</summary>
          <ol>
            {saved.reasoning.map((step, idx) => (
              <li key={idx}>
                <strong>{step.q}</strong> → {step.a}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
      <div className={css.elem("current-actions").toClassName()}>
        <Button type="button" look="outlined" size="small" onClick={onClear} disabled={busy}>
          Clear license
        </Button>
      </div>
    </SettingsCard>
  );
};

export const LicensingSettings = () => {
  // Org policy: consent.require_license enforced -> exports/pushes are
  // blocked until a license is chosen; surface that here, where the
  // fix lives.
  const [licenseRequired, setLicenseRequired] = useState(false);
  useEffect(() => {
    const pid = window.location.pathname.match(/projects\/(\d+)/)?.[1];
    if (!pid) return;
    fetch(`/api/projects/${pid}/effective-policies/`, { credentials: "same-origin", cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        const p = b?.policies?.["consent.require_license"];
        setLicenseRequired(!!(p?.enforced && p?.value));
      })
      .catch(() => {});
  }, []);

  const { t } = useTranslation();
  const { project, fetchProject } = useContext(ProjectContext);
  const api = useAPI();

  const saved = useMemo(() => project?.license || {}, [project?.license]);
  const [cursor, setCursor] = useState("start");
  const [trail, setTrail] = useState([]); // [{q,a,reasoning,nodeId}, ...]
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // If the project already has a saved license, start the wizard
  // collapsed (the CurrentLicenseCard at the top is the primary
  // surface). Owner can hit "Re-run wizard" to expand it.
  const [wizardOpen, setWizardOpen] = useState(() => !saved.identifier);

  // ``resultKey`` is the terminal license whose details the user is
  // about to confirm (or null if they're still answering questions).
  const [resultKey, setResultKey] = useState(null);

  const restart = useCallback(() => {
    setCursor("start");
    setTrail([]);
    setResultKey(null);
    setError("");
  }, []);

  const pick = useCallback(
    (option) => {
      setError("");
      const node = TREE[cursor];
      const newTrailStep = { q: node.text, a: option.label, reasoning: option.reasoning, nodeId: cursor };
      const nextTrail = [...trail, newTrailStep];
      setTrail(nextTrail);
      if (option.next.startsWith("result:")) {
        setResultKey(option.next.slice("result:".length));
      } else {
        setCursor(option.next);
      }
    },
    [cursor, trail],
  );

  // Step back to the question at trail index ``idx`` and forget every
  // answer after it. Lets the user revise an earlier branch without
  // re-walking unchanged questions.
  const jumpTo = useCallback(
    (idx) => {
      if (idx < 0 || idx >= trail.length) return;
      const stepNodeId = trail[idx].nodeId;
      setTrail(trail.slice(0, idx));
      setCursor(stepNodeId);
      setResultKey(null);
      setError("");
    },
    [trail],
  );

  const saveLicense = useCallback(async () => {
    if (!resultKey || !LICENSES[resultKey]) return;
    setBusy(true);
    setError("");
    const license = LICENSES[resultKey];
    const payload = {
      ...license,
      reasoning: trail.map((s) => ({ q: s.q, a: s.a, code: s.reasoning })),
      chosen_at: new Date().toISOString(),
      // chosen_by is set by the backend — the API has the requester
      // user; we don't need to round-trip it from the client.
    };
    const r = await api.callApi("updateProject", {
      params: { pk: project.id },
      body: { license: payload },
    });
    setBusy(false);
    if (!r || r.error) {
      setError(r?.error || "Couldn't save the license. Try again.");
      return;
    }
    if (typeof fetchProject === "function") fetchProject();
    setWizardOpen(false);
    setResultKey(null);
    setTrail([]);
    setCursor("start");
  }, [api, project?.id, resultKey, trail, fetchProject]);

  const clearLicense = useCallback(async () => {
    setBusy(true);
    setError("");
    const r = await api.callApi("updateProject", {
      params: { pk: project.id },
      body: { license: {} },
    });
    setBusy(false);
    if (!r || r.error) {
      setError(r?.error || "Couldn't clear the license.");
      return;
    }
    if (typeof fetchProject === "function") fetchProject();
    setWizardOpen(true);
    restart();
  }, [api, project?.id, restart, fetchProject]);

  // The current question (only meaningful when no result is pending).
  const node = !resultKey ? TREE[cursor] : null;
  const license = resultKey ? LICENSES[resultKey] : null;

  return (
    <div className={css.toClassName()}>
      <SettingsPageHeader
        title={t("pages.settings.licensing_settings.licensing")}
        description={t("pages.settings.licensing_settings.choose_how_this_dataset_can_be_reused_th")}
      />
      {licenseRequired && (
        <p
          style={{
            margin: "0 0 12px",
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-warning-content, #b26a00)",
          }}
        >
          Your organization requires a chosen license before exports or Hugging Face pushes — pick one below to unblock
          them.
        </p>
      )}

      {error ? (
        <Message variant="negative" closable onClose={() => setError("")}>
          {error}
        </Message>
      ) : null}

      <CurrentLicenseCard saved={saved} onClear={clearLicense} busy={busy} />

      {!wizardOpen && saved.identifier ? (
        <SettingsCard
          title={t("pages.settings.licensing_settings.rerun_the_wizard")}
          description={t("pages.settings.licensing_settings.pick_a_different_license_your_previous_c")}
        >
          <div className={css.elem("recommend-actions").toClassName()}>
            <Button
              type="button"
              variant="primary"
              size="small"
              onClick={() => {
                restart();
                setWizardOpen(true);
              }}
            >
              Start wizard
            </Button>
          </div>
        </SettingsCard>
      ) : null}

      {wizardOpen ? (
        <SettingsCard
          title={t("pages.settings.licensing_settings.decision_tree")}
          description={t("pages.settings.licensing_settings.answer_the_questions_below_at_most_five_")}
        >
          <ReasoningTrail trail={trail} onJumpTo={jumpTo} />
          {license ? (
            <RecommendationCard license={license} onSave={saveLicense} onRestart={restart} saving={busy} />
          ) : node ? (
            <div className={css.elem("question").toClassName()}>
              <p className={css.elem("question-text").toClassName()}>
                {t(`pages.settings.licensing.${cursor}.text`, undefined, node.text)}
              </p>
              {node.hint ? (
                <p className={css.elem("question-hint").toClassName()}>
                  {t(`pages.settings.licensing.${cursor}.hint`, undefined, node.hint)}
                </p>
              ) : null}
              <div className={css.elem("question-options").toClassName()}>
                {node.options.map((opt, idx) => (
                  <OptionButton key={idx} onClick={() => pick(opt)}>
                    {opt.label}
                  </OptionButton>
                ))}
              </div>
            </div>
          ) : null}
        </SettingsCard>
      ) : null}
    </div>
  );
};

LicensingSettings.title = "Licensing";
LicensingSettings.path = "/licensing";
LicensingSettings.exact = true;
